import { cevir } from "@/lib/dil";

/**
 * D-075: Finishing'de assembly kuyruğunda olmayan riserin satırı; ekranın saf yardımcıları.
 *
 * Kuyruk dışı riser ("muayeneden" grubu: en az bir komponenti tamam, kuyruk zamanı boş) üstteki notla riserin gerçekten
 * çıkabileceği ekranı gösterir. Repair planning listesinde mi (`planlamada`; listenin kendi kuralı) ve muayenenin bitip bitmediği
 * (`muayene.biten === muayene.toplam`) SUNUCUDAN gelir (assembly.controller.ts `liste()`); burada yalnız metne ve tona çevrilir.
 * Coating yükleme düğmesi kuyruk dışı riserde pasiftir (sunucu reddeder: "Riser not sent to assembly."); açılmış kuyruk dışı
 * satırda NDT bandı çıkmaz (o riseri tutan kuyruktur). Repair planning'deki aynı satır bileşeni etkilenmez.
 *
 * Cümleler `cevir("…")` ile kurulur; Türkçeleri docs/ceviri/tr-elle.tsv'de `# U-074 (D-075)` başlığı altındadır.
 */

/** Kuyruk dışı satırın sunucudan gelen alanları (`planlamada` ve `muayene` eski yanıtta yoksa bugünkü not çizilir). */
export interface KuyrukDisiSatir {
  planlamada?: boolean;
  muayene?: { biten: number; toplam: number; setRn?: string };
}

/** Kuyruk dışı satırın üst notu: metin, ton ve yanında triyaj bağlantısının çizilip çizilmeyeceği. */
export type KuyrukDisiNotu = { metin: string; ton: "warn" | "bad"; triyaj: boolean };

/**
 * Üç not (D-075 (2)):
 * - Repair planning listesindeyse (ya da eski yanıtta bilgi yoksa) var olan sarı "not queued — "Send to assembly" from Repair planning".
 * - Listede değilse ve muayenede tamam olmayan komponent varsa sarı "not queued — queued when inspection is complete" ve triyaj bağlantısı.
 * - Listede değilse ve görünür komponentlerin hepsi tamamsa kırmızı "not queued although inspection is complete" ve triyaj bağlantısı
 *   (çıkışı olmayan, takılı riser; K4'ün sayımı bu nottan yapılır).
 * Bağlantı yalnız muayene setinin numarası (`muayene.setRn`) varken çizilir.
 */
export function kuyrukDisiNotu(s: KuyrukDisiSatir): KuyrukDisiNotu {
  if (s.planlamada !== false) return { metin: cevir("not queued — \"Send to assembly\" from Repair planning"), ton: "warn", triyaj: false };
  const triyaj = Boolean(s.muayene?.setRn);
  if (s.muayene && s.muayene.toplam > 0 && s.muayene.biten === s.muayene.toplam) return { metin: cevir("not queued although inspection is complete"), ton: "bad", triyaj };
  return { metin: cevir("not queued — queued when inspection is complete"), ton: "warn", triyaj };
}

/** Riserin assembly kuyruğu kaydı (`set_durum`; kuyruk dışı satırda null). */
type SetKaydi = { gonderimTs: string | null; asmRaporu?: unknown } | null | undefined;

/** D-075 (3): Finishing satırında riser kuyrukta değilse "Upload Coating report" pasiftir. Repair planning modunda hiçbir şey değişmez. */
export const coatingKapali = (mod: "plan" | "asm", set: SetKaydi): boolean => mod === "asm" && !set?.gonderimTs;

/** Pasif Coating düğmesinin ipucu (evrak yetkisi olmayan kullanıcı bunun yerine yetki metnini görür; `Buton`un kuralı). */
export const coatingKapaliIpucu = (): string => cevir("Opens when the riser is in the assembly queue.");

/**
 * Açılmış satırdaki "Assembly report upload is blocked: NDT …" bandı: Finishing satırında, Assembly raporu yokken, eksik NDT hattı
 * varken ve (D-075) riser kuyruktayken. Kuyruk dışı satırda bant çıkmaz: Finishing adımını tutan kuyruktur, arşiv Assembly raporu da
 * NDT koşulu aranmadan yüklenir; NDT durumu satırdaki "NDT x/5 lines" etiketinde kalır. NDT kuralı değişmez.
 */
export const ndtBandiGorunur = (mod: "plan" | "asm", set: SetKaydi, eksikHat: number): boolean =>
  mod === "asm" && !set?.asmRaporu && eksikHat > 0 && Boolean(set?.gonderimTs);
